/**
 * service-template.ts
 * Shared Zod contracts for LOCX service templates (add-locx-service-templates, SL-73..77).
 *
 * A ServiceTemplateDefinition is a resolved SNAPSHOT — not new expressiveness. It mirrors
 * the exact closed shape field_definition/field_option already enforce (same DataTypeSchema
 * enum, same bounded-validation columns, no formulas, no nesting). rate_card and asset_type
 * data are deliberately absent (proposal.md out-of-scope decisions).
 *
 * `workflow` is intentionally NOT part of this shape — it is always re-derived from
 * `rentalKind` via deriveWorkflow() at instantiate time, the same rule the existing
 * POST /servicios/service-types route already follows (design.md §2 revision note).
 *
 * Spec: openspec/changes/add-locx-service-templates/design.md §2, spec.md SL-73/74/75
 */
import { z } from 'zod';
import { DataTypeSchema } from './configurable-engine.js';
import { DefaultItemsSchema, rentalKindSchema } from './service-order.js';

// ---------------------------------------------------------------------------
// ServiceTemplateDefinition — the jsonb snapshot stored in service_template.definition
// ---------------------------------------------------------------------------

export const ServiceTemplateFieldOptionSchema = z
  .object({
    value: z.string().min(1),
    label: z.string().min(1),
    position: z.number().int(),
  })
  .strict();

export type ServiceTemplateFieldOption = z.infer<typeof ServiceTemplateFieldOptionSchema>;

/**
 * One field_definition, captured as the RESOLVED effective shape (FieldResolver output),
 * never raw base+override rows — SL-74.1.
 */
export const ServiceTemplateFieldSchema = z
  .object({
    key: z.string().min(1).max(128),
    label: z.string().min(1).max(256),
    dataType: DataTypeSchema,
    required: z.boolean(),
    defaultValue: z.string().nullable(),
    position: z.number().int(),
    isGridColumn: z.boolean(),
    gridPosition: z.number().int().nullable(),
    minValue: z.string().nullable(),
    maxValue: z.string().nullable(),
    maxLength: z.number().int().nullable(),
    pattern: z.string().nullable(),
    // Only populated for dataType === 'select' — enforced by the superRefine below.
    options: z.array(ServiceTemplateFieldOptionSchema),
  })
  .strict()
  .superRefine((field, ctx) => {
    if (field.dataType === 'select' && field.options.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "select fields must have at least one option",
        path: ['options'],
      });
    }
    if (field.dataType !== 'select' && field.options.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "options are only allowed on 'select' fields",
        path: ['options'],
      });
    }
  });

export type ServiceTemplateField = z.infer<typeof ServiceTemplateFieldSchema>;

/**
 * service_type shape captured by the template. `code` is a suggested default —
 * instantiate() may override it (SL-75.2). `workflow` is deliberately absent (see
 * file header) — only `rentalKind` is stored; workflow is re-derived every time.
 */
export const ServiceTemplateServiceTypeSchema = z
  .object({
    code: z.string().min(1).max(24),
    label: z.string().min(1).max(256),
    rentalKind: rentalKindSchema,
    dailyReportEnabled: z.boolean(),
    defaultItems: DefaultItemsSchema.nullable(),
  })
  .strict();

export type ServiceTemplateServiceType = z.infer<typeof ServiceTemplateServiceTypeSchema>;

export const ServiceTemplateDefinitionSchema = z
  .object({
    serviceType: ServiceTemplateServiceTypeSchema,
    fields: z.array(ServiceTemplateFieldSchema),
  })
  .strict();

export type ServiceTemplateDefinition = z.infer<typeof ServiceTemplateDefinitionSchema>;

// ---------------------------------------------------------------------------
// Request contracts
// ---------------------------------------------------------------------------

/**
 * Request body for POST /platform/service-templates (extract_to_template).
 * companyId IS in the body here — unlike a tenant route, a platform request carries
 * no tenant scope to infer the SOURCE company from (design.md §3).
 */
export const ServiceTemplateExtractSchema = z
  .object({
    serviceTypeId: z.string().uuid('serviceTypeId must be a valid UUID'),
    companyId: z.string().uuid('companyId must be a valid UUID'),
    key: z.string().min(1).max(128),
    label: z.string().min(1).max(256),
    description: z.string().max(2000).optional(),
  })
  .strict();

export type ServiceTemplateExtract = z.infer<typeof ServiceTemplateExtractSchema>;

/**
 * Request body for POST /platform/companies/:id/service-templates/:key/instantiate.
 * companyId comes from the :id path param (KI-33.4 convention) — never here.
 * version resolution ('latest' vs a specific number) is a query/path concern, not body.
 */
export const ServiceTemplateInstantiateSchema = z
  .object({
    codeOverride: z.string().min(1).max(24).optional(),
  })
  .strict();

export type ServiceTemplateInstantiate = z.infer<typeof ServiceTemplateInstantiateSchema>;

// ---------------------------------------------------------------------------
// Response contracts
// ---------------------------------------------------------------------------

export const ServiceTemplateAdminResponseSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  version: z.number().int(),
  label: z.string(),
  description: z.string().nullable(),
  definition: ServiceTemplateDefinitionSchema,
  sourceCompanyId: z.string().uuid().nullable(),
  status: z.enum(['active', 'archived']),
  createdBy: z.string().uuid(),
  createdAt: z.coerce.date(),
});

export type ServiceTemplateAdminResponse = z.infer<typeof ServiceTemplateAdminResponseSchema>;

/**
 * Lean list-row shape for GET /platform/service-templates — omits `definition`
 * (a full field-by-field snapshot has no place in a catalog table row) and
 * `createdBy`/`sourceCompanyId` (provenance, not needed for the "pick a
 * template" UI). One row per key — the latest ACTIVE version only.
 */
export const ServiceTemplateListItemSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  label: z.string(),
  version: z.number().int(),
  description: z.string().nullable(),
  status: z.enum(['active', 'archived']),
  createdAt: z.coerce.date(),
});

export type ServiceTemplateListItem = z.infer<typeof ServiceTemplateListItemSchema>;

/**
 * Row shape for GET /platform/companies/:id/service-types — lets a platform admin
 * pick a SOURCE service_type to extract into a template (SL-74 UI). Lean: only what
 * the picker needs, no field-level data (FieldResolver runs at extract time, not here).
 */
export const ServiceTypeExtractionCandidateSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  rentalKind: rentalKindSchema,
});

export type ServiceTypeExtractionCandidate = z.infer<typeof ServiceTypeExtractionCandidateSchema>;
